// ===========================================
// Signature Pad
// ===========================================
// Drawn signature capture, with a typed cursive fallback.
//
// NO LIBRARY. Nothing suitable is installed (no signature_pad, no
// react-signature-canvas, no fabric/konva), and the whole capture is ~60 lines
// of pointer events — a dependency here would be more code to audit than to
// write, on a surface that produces a legal artefact.
//
// OUTPUT IS AN SVG PATH, NOT A PNG. It is a fraction of the size, scales
// crisply onto the PDF at any DPI, and stores in a column rather than needing
// the storage service. pdfkit renders it directly via doc.path().
//
// ⚠️ The rendered mark is PRESENTATION. The legal record is the audit envelope
// the server records alongside it — signer name, timestamp, IP, user agent —
// which is why the confirm step asks for a typed name in both modes.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';

/** Cursive faces offered for a typed signature. Keys are persisted, so never renumber. */
export const SIGNATURE_FONTS = [
  { key: 'great-vibes', label: 'Flowing', css: "'Great Vibes', cursive" },
] as const;

export type SignatureMethod = 'DRAWN' | 'TYPED';

export interface SignatureValue {
  method: SignatureMethod;
  /** SVG path data, DRAWN only. */
  signatureSvg?: string;
  /** Typed text, TYPED only. */
  signatureText?: string;
  /** Which cursive face rendered the typed text. */
  signatureFontKey?: string;
  /** Always required — the typed name is part of the audit record in BOTH modes. */
  signedByName: string;
}

interface SignaturePadProps {
  value: SignatureValue | null;
  onChange: (value: SignatureValue | null) => void;
  disabled?: boolean;
  /**
   * The signer's full name, owned by the PARENT component.
   *
   * It lives up there rather than in here because it gates the whole form: `emit()` treats it
   * as its first condition, so with it empty every "Give permission" button stays disabled no
   * matter how good the signature is. Rendering it inside this component put it at the BOTTOM
   * of the page, below the per-student sections whose buttons it controls — so a parent filled
   * in a child's answers, found the button dead, and had no idea the cause was a field further
   * down. Hoisting it lets the form ask for it once, up front.
   */
  signerName: string;
}

const CANVAS_HEIGHT = 160;
const STROKE_WIDTH = 2;

/**
 * Minimum pointer travel (CSS px) before a point is recorded.
 *
 * A high-frequency stylus emits ~240 points/second; 30 seconds of drawing would
 * otherwise store ~7,000 points. Those points reach the server as one string,
 * get persisted to `@db.Text`, and are re-parsed by pdfkit on every render.
 * Decimating within 1px is visually lossless at this stroke width.
 */
const MIN_POINT_DISTANCE = 1;

/**
 * Hard ceiling on total points per signature.
 *
 * QA (Sprint 4) measured a ~10MB path costing ~1.8s of event-loop-blocking CPU
 * and ~385MB heap server-side. The server must cap this too — a client limit is
 * a courtesy, not a control — but stopping here means an honest signature never
 * approaches the limit in the first place.
 */
const MAX_POINTS = 4000;

export default function SignaturePad({
  value,
  onChange,
  disabled,
  signerName,
}: SignaturePadProps) {
  // DRAWN is the default: it is what a parent expects from a signature box, and
  // typed is offered as the fallback for anyone on a device where drawing is
  // awkward.
  const [method, setMethod] = useState<SignatureMethod>(value?.method ?? 'DRAWN');
  const [typedName, setTypedName] = useState(value?.signatureText ?? '');
  // The signer's name is a PROP now (rendered at the top of the form), not local state.
  const name = signerName;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  // Completed strokes plus the one in progress. Kept as points (not a flattened
  // path string) so undo can drop a whole stroke.
  const strokesRef = useRef<Array<Array<{ x: number; y: number }>>>([]);
  const [hasDrawing, setHasDrawing] = useState(false);

  // --- canvas plumbing ----------------------------------------------------

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.lineWidth = STROKE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1a1a2e';

    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i += 1) ctx.lineTo(stroke[i].x, stroke[i].y);
      ctx.stroke();
    }
    ctx.restore();
  }, []);

  // Width the strokes were captured against. Used to rescale on resize.
  const captureWidthRef = useRef<number>(0);

  // Size the backing store to the device pixel ratio, or strokes look furry on
  // phones — which is where most of these will be signed.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();

      // ⚠️ RESCALE STORED POINTS. Coordinates are CSS pixels captured against
      // the width at the time of drawing. The canvas is width:100%, so a phone
      // rotating mid-signature (portrait ~360px → landscape ~740px) would
      // otherwise replay old points against the new width — and because
      // `toSvgPath()` emits those same stored points, the DISTORTION IS BAKED
      // INTO THE PDF, not just the on-screen preview. (QA, Sprint 4.)
      const prevWidth = captureWidthRef.current;
      const scale = prevWidth > 0 ? rect.width / prevWidth : 1;
      // Guard the SCALE, not just the widths. A zero-width canvas (hidden, or
      // not yet laid out — and always in jsdom) makes the ratio Infinity or
      // NaN, which would corrupt every stored coordinate and produce a path of
      // `MNaN,NaN` that pdfkit then refuses. Only rescale when the factor is a
      // real, finite, meaningful change.
      if (
        Number.isFinite(scale) &&
        scale > 0 &&
        rect.width > 0 &&
        Math.abs(rect.width - prevWidth) > 0.5
      ) {
        for (const stroke of strokesRef.current) {
          for (const p of stroke) p.x *= scale;
        }
      }
      // Only remember a real width, or the next resize divides by zero again.
      if (rect.width > 0) captureWidthRef.current = rect.width;

      canvas.width = rect.width * dpr;
      canvas.height = CANVAS_HEIGHT * dpr;
      redraw();

      // A rotation never fires pointerup, so an in-progress stroke would stay
      // open and keep appending against the new geometry.
      drawingRef.current = false;
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('orientationchange', resize);
    };
  }, [redraw]);

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  /**
   * Convert captured strokes to an SVG path.
   *
   * One `M` per stroke so lifting the pen produces a genuine break rather than
   * a connecting line across the signature.
   */
  const toSvgPath = (): string =>
    strokesRef.current
      .filter((s) => s.length > 1)
      .map(
        (stroke) =>
          `M${stroke[0].x.toFixed(1)},${stroke[0].y.toFixed(1)} ` +
          stroke
            .slice(1)
            .map((p) => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`)
            .join(' ')
      )
      .join(' ');

  const emit = useCallback(
    (nextName: string, nextTyped: string, nextMethod: SignatureMethod) => {
      if (!nextName.trim()) {
        onChange(null);
        return;
      }

      if (nextMethod === 'DRAWN') {
        const path = toSvgPath();
        if (!path) {
          onChange(null);
          return;
        }
        onChange({ method: 'DRAWN', signatureSvg: path, signedByName: nextName.trim() });
        return;
      }

      if (!nextTyped.trim()) {
        onChange(null);
        return;
      }
      onChange({
        method: 'TYPED',
        signatureText: nextTyped.trim(),
        signatureFontKey: SIGNATURE_FONTS[0].key,
        signedByName: nextName.trim(),
      });
    },
    [onChange]
  );

  // The name now lives at the TOP of the form, so it can change while a signature already
  // exists down here. Re-emit when it does: without this, a parent who signs first and then
  // fills in their name would still see every button disabled, because `emit` last ran with
  // an empty name and nothing would call it again.
  const lastEmittedName = useRef(signerName);
  useEffect(() => {
    if (lastEmittedName.current === signerName) return;
    lastEmittedName.current = signerName;
    emit(signerName, typedName, method);
  }, [signerName, typedName, method, emit]);

  // --- pointer handlers ---------------------------------------------------

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    strokesRef.current.push([pointFromEvent(e)]);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || disabled) return;

    const stroke = strokesRef.current[strokesRef.current.length - 1];
    const point = pointFromEvent(e);

    // Decimate: skip points closer than a pixel to the last one. A stylus emits
    // hundreds per second and every one of them ends up in the stored path.
    const last = stroke[stroke.length - 1];
    if (last) {
      const dx = point.x - last.x;
      const dy = point.y - last.y;
      if (dx * dx + dy * dy < MIN_POINT_DISTANCE * MIN_POINT_DISTANCE) return;
    }

    // Stop growing rather than truncating mid-render: the drawing simply stops
    // responding, which is visible, instead of silently losing the tail.
    const total = strokesRef.current.reduce((n, s) => n + s.length, 0);
    if (total >= MAX_POINTS) return;

    stroke.push(point);
    redraw();
  };

  const handlePointerUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    setHasDrawing(strokesRef.current.some((s) => s.length > 1));
    emit(name, typedName, method);
  };

  const handleUndo = () => {
    strokesRef.current.pop();
    redraw();
    setHasDrawing(strokesRef.current.some((s) => s.length > 1));
    emit(name, typedName, method);
  };

  const handleClear = () => {
    strokesRef.current = [];
    redraw();
    setHasDrawing(false);
    emit(name, typedName, method);
  };

  // --- render -------------------------------------------------------------

  return (
    <Stack spacing={2}>
      <Tabs
        value={method}
        onChange={(_, v: SignatureMethod) => {
          setMethod(v);
          emit(name, typedName, v);
        }}
        aria-label="Signature method"
      >
        <Tab label="Draw" value="DRAWN" disabled={disabled} />
        <Tab label="Type" value="TYPED" disabled={disabled} />
      </Tabs>

      {method === 'DRAWN' ? (
        <Box>
          <Box
            component="canvas"
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            aria-label="Signature drawing area"
            sx={{
              width: '100%',
              height: `${CANVAS_HEIGHT}px`,
              border: '1px solid',
              borderColor: hasDrawing ? 'primary.main' : 'grey.400',
              borderRadius: 1,
              bgcolor: 'background.default',
              // Stops the browser scrolling the page while a finger draws —
              // without this, signing on a phone is nearly impossible.
              touchAction: 'none',
              cursor: disabled ? 'not-allowed' : 'crosshair',
            }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" startIcon={<UndoIcon />} onClick={handleUndo} disabled={disabled || !hasDrawing}>
              Undo
            </Button>
            <Button size="small" onClick={handleClear} disabled={disabled || !hasDrawing}>
              Clear
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box>
          <TextField
            fullWidth
            label="Type your signature"
            value={typedName}
            onChange={(e) => {
              setTypedName(e.target.value);
              emit(name, e.target.value, method);
            }}
            disabled={disabled}
            inputProps={{ maxLength: 100 }}
          />
          <Box
            sx={{
              mt: 1.5,
              minHeight: 70,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              border: '1px solid',
              borderColor: 'grey.400',
              borderRadius: 1,
              bgcolor: 'background.default',
              fontFamily: SIGNATURE_FONTS[0].css,
              fontSize: 34,
              color: '#1a1a2e',
              // Long names must shrink rather than overflow — the PDF renderer
              // does the same, so preview and artefact stay consistent.
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {typedName || (
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Inter' }}>
                Your signature will appear here
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
